import type {
  WidgetConfig,
  WidgetWeekEntry,
  WorkdayLogConfig,
  WorkdayLogDayData,
} from "@/lib/schema";
import { countPhrase, sumOverDays } from "@/widgets/describe";
import { getWidgetModule } from "@/widgets/registry";
import type { DayWidgetModule, ScorablePart } from "@/widgets/types";
import { DayView } from "./DayView";
import { SettingsEditor } from "./SettingsEditor";
import {
  maxScoreWeek,
  scoreDay,
  scoreWeek,
  scoreWeekHours,
  scoreWeekPeriods,
} from "./scoring";
import { validate } from "./validate";

function defaults(existing: WidgetConfig[]): WorkdayLogConfig {
  const dayWidgets = existing.filter(
    (w) => getWidgetModule(w.kind)?.scope === "day",
  );
  const maxRow = dayWidgets.reduce((m, w) => Math.max(m, w.row), -1);
  return {
    id: crypto.randomUUID(),
    kind: "workday-log",
    label: "Arbeidsdag",
    row: maxRow + 1,
    column: 0,
    enabled: true,
    targetDuration: "07:30",
    pointsAtTarget: 2,
    // Deliberately not half the target: 07:30 earns its two periods with
    // half an hour to spare, so a slightly short day keeps both.
    periodDuration: "03:30",
    pointsPerPeriod: 1,
    hoursWeekGoal: Number.NaN,
    periodsWeekGoal: Number.NaN,
    // Stays NaN until both parts are filled in, which is what gates Save.
    weekGoal: Number.NaN,
  };
}

/**
 * The two halves, separately trackable in a weekly point summary
 * (docs/adr/0010). Keys are stored inside sealed snapshots and must not
 * change.
 */
const parts: ScorablePart<WorkdayLogConfig>[] = [
  {
    key: "hours",
    label: "Arbeidstid",
    scoreWeek: scoreWeekHours,
    weekGoal: (config) => config.hoursWeekGoal,
  },
  {
    key: "periods",
    label: "Produktivitet",
    scoreWeek: scoreWeekPeriods,
    weekGoal: (config) => config.periodsWeekGoal,
  },
];

// A day is logged by either statement — hours worked, or Ikke jobbet. The
// Periode answers ride along with the hours and are never counted separately.
function describeWeekData(entry: WidgetWeekEntry): string | null {
  const days = sumOverDays<WorkdayLogDayData>(entry, (day) =>
    day?.duration || day?.notWorked ? 1 : 0,
  );
  return countPhrase(days, "dag logget", "dager logget");
}

export const workdayLog: DayWidgetModule<WorkdayLogConfig> = {
  kind: "workday-log",
  label: "Arbeidsdag",
  scope: "day",
  scoring: "points",
  defaults,
  validate,
  SettingsEditor,
  DayView,
  describeWeekData,
  scoreDay,
  scoreWeek,
  maxScoreWeek,
  parts,
};
